import { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { sortTableDateFechaInicio, sortTableDateFechaFin, sortTableStatusStatus } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { buscarAsesorados } from '../../actions/deportiva';

import FiltrosAsesorias from './FiltrosAsesorias';
import DetallesAsesoria from './DetallesAsesoria';

const Asesorados = () => {

    let rutasBread = [
        {
            activo: false,
            nombre: 'Citas y bloqueos',
            path: '/deportiva'
        },
        {
            activo: true,
            nombre: 'Asesorados',
            path: '/deportiva/historial'
        }
    ];

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { listado_asesorias } = useSelector(state => state.deportiva);

    const [accesos, setAccesos] = useState([]);
    const [busquedaData, setBusquedaData] = useState({
        id_joven: null,
        id_asesor: null, 
        fecha_inicio: null,
        fecha_fin: null, 
        mensajes: null, 
        status: [null, 0, 1, 2], 
        busqueda: false
    });

    const [asesoradoDetalles, setAsesoradoDetalles] = useState(null);

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const columns = [
        {
            name: 'Asesorado',
            sortable: true, 
            selector: row => row.joven ? [row.joven.nombre, row.joven.apellido_paterno, row.joven.apellido_materno].join(' ') : 'Sin definir', 
            grow: 2
        },
        {
            name: 'Última cita',
            sortable: true, 
            sortFunction: sortTableDateFechaInicio, 
            selector: row => row.ultima_cita ? new Date(row.ultima_cita).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir',
            grow: 2,
            wrap: true
        },
        {
            name: 'Próxima cita',
            sortable: true, 
            sortFunction: sortTableDateFechaFin, 
            selector: row => row.proxima_cita ? new Date(row.proxima_cita).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir',
            grow: 2,
            wrap: true
        },
        {
            name: 'Status de próxima cita',
            sortable: true, 
            grow: 1.5,
            sortFunction: sortTableStatusStatus, 
            selector: row => {
                switch(row.status){
                    case 0:
                        return(
                            <h5><Badge pill bg="warning text-black">Pendiente</Badge></h5>
                        )
                    case 1:
                        return(
                            <h5><Badge pill bg="success">Aprobada</Badge></h5>
                        )        
                    case 2:
                        return(
                            <h5><Badge pill bg="danger">Rechazada</Badge></h5>
                        )
                    default:
                        return(
                            <h5><Badge pill bg="secondary">Sin cita</Badge></h5>
                        )
                        break;
                }
            }
        }, 
        {
            name: 'Asesor actual',
            sortable: true, 
            selector: row => row.usuario ? [row.usuario.nombre, row.usuario.apellido_paterno, row.usuario.apellido_materno].join(' ') : 'Sin definir', 
            grow: 2
        }, 
        {
            name: 'Citas',
            sortable: true, 
            grow: 1, 
            selector: row => row.citas,
        },
        {
            name: 'Mensajes sin leer',
            sortable: true, 
            grow: 1.5, 
            selector: row => row.mensajes_sin_leer,
        },
        {
            name: '',
            grow: 0.3, 
            cell: row => {
                return(
                    <div className="text-right w-100">
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleDetallesAsesoria({ _id: row._id })}><i className="fas fa-eye text-info"></i></button>
                    </div>
                )
            }
        }
    ];

    /* Filtros */
    const handleFiltrosBusqueda = (busqueda) => {
        setAsesoradoDetalles(null);

        setBusquedaData({
            ...busqueda
        });
    }

    /* Detalles asesorado */
    const handleDetallesAsesoria = (asesorado) => {
        setBusquedaData({
            ...busquedaData, 
            busqueda: false
        });

        setAsesoradoDetalles(asesorado?._id || null);
    }

    /* Detalles completos asesorado */
    const handleDetallesCompletosAsesoria = (asesorado) => {
        setBusquedaData({
            ...busquedaData, 
            busqueda: false
        });
        setAsesoradoDetalles(null);
        
        navigate('/deportiva/detalles/' + asesorado);
    }

    useEffect(() => {
        dispatch(buscarAsesorados(busquedaData));
    }, []);

    useEffect(() => {
        setAccesos(listado_asesorias);
    }, [listado_asesorias]);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);    

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .card-subtitle {
                            font-size: 1.5rem;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <FiltrosAsesorias busqueda={busquedaData} reestablecerFiltros={handleFiltrosBusqueda} />
            <DetallesAsesoria asesoria={asesoradoDetalles} reestablecerAsesoria={handleDetallesAsesoria} detallesCompletosAsesoria={handleDetallesCompletosAsesoria} />

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="mb-3 mt-5">Historial de asesorados</h3>

                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn btn-primary me-2" onClick={() => handleFiltrosBusqueda({ ...busquedaData, busqueda: true })}>Filtros de búsqueda</button>
                    </div>

                    <div className="row my-5">
                        <DataTable 
                            columns={columns}
                            data={accesos}
                            pagination
                            paginationPerPage={20}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover={true}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Asesorados;